import { FC, useEffect, useState, useMemo, useCallback } from 'react'
import { Controller, useForm, RegisterOptions } from 'react-hook-form'

import {
    ColumnLayout,
    Container,
    FormField,
    Input,
    Link,
    RadioGroup,
    SpaceBetween,
    Header,
    Alert,
    Button,
    Box,
    Grid
} from '@cloudscape-design/components'

import FormInput from '../../../ControlledFormInputs/Input'
import useAccounts from '../../../../hooks/query/useAccounts'

import { useAccountCreationFormStore } from '../../../../stores/accounts/newAccountCreationFormStore'
import { StepConfig } from '../steps_config'

const step2Fields: Array<StepConfig> = [
    {
        label: 'Distribution List',
        fieldName: 'DistributionList',
        description:
            "The Distribution List will be used as the AWS account email and the owner of the DL will have root access. Here's standard format for the AWS Tower 2 account email: aws-glo02-<group_name>-<platform_name>-<environment>@globe.com.ph",
        info: (
            <Link
                external
                href="https://globetelecom.atlassian.net/wiki/spaces/globeinfra/pages/4512187069/Account+Management"
            >
                Valid Distribution List Link
            </Link>
        ),
        type: 'text'
    },
    {
        label: 'Budget Threshold Alert',
        fieldName: 'MonthlyBudgetCap',
        description:
            'Kindly place the Initial Monthly Budget Cap that you would like each account to have as part of Account Creation. The Service Owner can still update the Budget Cap as necessary via the pipeline after account creation. Kindly ensure that the Budget Cap indicated here is aligned with the approved budget allocated for your particular Project/Workload.',
        type: 'text',
        constraintText: 'Value should be in USD. Example: 100 (means 100USD)'
    }
]

type SSOUserEntry = { userName: string; displayName: string }

function normalizeSSOUserNamesList(
    value: unknown
): Array<SSOUserEntry> {
    if (Array.isArray(value)) {
        return value.map((item) =>
            typeof item === 'object' && item !== null && 'userName' in item
                ? {
                    userName: String((item as SSOUserEntry).userName ?? ''),
                    displayName: String((item as SSOUserEntry).displayName ?? '')
                }
                : { userName: '', displayName: '' }
        )
    }
    if (typeof value === 'string' && value.trim()) {
        return value.split(',').map((s) => ({
            userName: s.trim(),
            displayName: ''
        }))
    }
    return []
}

const Step2: FC = () => {
    const {
        formValues,
        stgFollowUpReadOnly,
        actions: { setFormValues, setFormValidity }
    } = useAccountCreationFormStore()

    const {
        control,
        formState: { isValid },
        trigger,
        getValues
    } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: formValues
    })

    // Fetch all accounts for validation
    const { data: accounts } = useAccounts()

    // Collect all distribution lists from form values
    const allDistributionLists = useMemo(() => {
        const lists: Record<string, string> = {}
        formValues.accountTypes?.forEach((accountType: { value: string }) => {
            const fieldName = `${accountType.value}DistributionList`
            const value = formValues[fieldName]
            if (value) {
                lists[fieldName] = value
            }
        })
        return lists
    }, [formValues])

    // Check if any distribution list has REJECTED status for warning display
    const [rejectedDistributionLists, setRejectedDistributionLists] = useState<
        Record<string, any>
    >({})

    // Validate distribution list against existing accounts
    // This validation is applied to EACH selected account type's distribution list field
    // For example, if user selected Sandbox + Production, both SandboxDistributionList and ProductionDistributionList are validated
    // If ANY distribution list has validation errors, the entire form is marked invalid and Next button is disabled
    const validateDistributionList = useCallback(
        (value: string): boolean | string => {
            if (!value) return true

            // Check pattern first
            const patternRegex = /^aws-glo02-[a-zA-Z]+-[a-zA-Z0-9]+-[a-zA-Z]+@globe\.com\.ph$/
            if (!patternRegex.test(value)) {
                return '❌ Invalid distribution list format. Required format: aws-glo02-<group_name>-<platform_name>-<environment>@globe.com.ph'
            }

            // Ensure accounts data is loaded before checking
            if (!accounts) {
                return 'Loading account data...'
            }

            // Check if distribution list exists in accounts with blocking status
            const existingAccount = accounts?.find((account) => {
                const dl = account.details?.distributionList
                return dl === value
            })

            if (existingAccount?.status === 'PENDING_APPROVAL') {
                return '⛔ An account onboarding request is already pending approval for this distribution list. Please wait for approval before submitting a new request.'
            }

            if (existingAccount?.status === 'APPROVED') {
                return '⛔ An account onboarding request has already been approved for this distribution list. Duplicate requests cannot be submitted.'
            }

            return true
        },
        [accounts]
    )

    // Trigger re-validation when accounts data changes
    useEffect(() => {
        if (accounts && accounts.length > 0) {
            // Re-validate all distribution list fields
            formValues.accountTypes?.forEach((accountType: { value: string }) => {
                const fieldName = `${accountType.value}DistributionList`
                const fieldValue = getValues(fieldName)
                if (fieldValue) {
                    trigger(fieldName)
                }
            })
        }
    }, [accounts, trigger, getValues, formValues.accountTypes])

    useEffect(() => {
        const rejectedLists: Record<string, any> = {}

        Object.entries(allDistributionLists).forEach(([fieldName, distList]) => {
            const existingAccount = accounts?.find((account) => {
                return account.details?.distributionList === distList
            })

            if (existingAccount?.status === 'REJECTED') {
                rejectedLists[fieldName] = {
                    warningMessage: `⚠️ Your previous request for this distribution list was rejected. You can now submit a new request.`,
                    existingAccount
                }
            }
        })

        setRejectedDistributionLists(rejectedLists)
    }, [accounts, allDistributionLists])

    // SSO user-names: at least one entry; each entry must have both Network_ID and Display_Name
    const ssoValidation = useMemo(() => {
        const errors: Record<string, string> = {}
        const emailPattern = /^[a-zA-Z0-9._%+-]+@globetel\.com$/i
        formValues.accountTypes?.forEach((accountType: { value: string }) => {
            const list = normalizeSSOUserNamesList(formValues[`${accountType.value}SSOUserNames`])
            const filled = list.filter((e) => (e.userName ?? '').trim() || (e.displayName ?? '').trim())
            if (filled.length === 0 && list.length === 0) {
                errors[accountType.value] = 'Add at least one SSO user with Network ID and Display Name.'
                return
            }
            if (filled.length === 0) {
                errors[accountType.value] = 'At least one SSO user is required.'
                return
            }
            const invalid = filled.some(
                (e) =>
                    !(e.userName ?? '').trim() ||
                    !(e.displayName ?? '').trim() ||
                    !emailPattern.test((e.userName ?? '').trim())
            )
            if (invalid) {
                errors[accountType.value] =
                    'For each SSO user, both Network ID (e.g. user@globetel.com) and Display Name are required.'
            }
        })
        const ssoIsValid = Object.keys(errors).length === 0
        return { ssoIsValid, errors }
    }, [formValues])

    // Update form validity: react-hook-form + SSO validation
    useEffect(() => {
        setFormValidity(isValid && ssoValidation.ssoIsValid)
    }, [isValid, ssoValidation.ssoIsValid, setFormValidity])

    useEffect(() => {
        if (stgFollowUpReadOnly) {
            setFormValidity(true)
        }
    }, [stgFollowUpReadOnly, setFormValidity])

    const getFieldTypeRules = (fieldType: string, fieldName: string): RegisterOptions => {
        switch (fieldType) {
            case 'text':
                if (fieldName === 'MonthlyBudgetCap') {
                    return {
                        required: true,
                        pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: 'Invalid Budget'
                        }
                    }
                }

                if (fieldName === 'DistributionList') {
                    return {
                        required: true,
                        validate: validateDistributionList
                    }
                }

                return {
                    required: true,
                    pattern: {
                        value: /^aws-glo02-[a-zA-Z]+-[a-zA-Z0-9]+-[a-zA-Z]+@globe\.com\.ph$/,
                        message: 'Invalid email address'
                    }
                }

            case 'number':
                return {
                    required: true,
                    min: {
                        value: 0,
                        message: 'Value should be greater than 0'
                    },
                    max: {
                        value: 100000,
                        message: 'Value should be less than 1000000'
                    }
                }

            case 'radio':
                return {}

            default:
                return {
                    required: true
                }
        }
    }

    const accountWorkloadType = formValues.accountWorkloadType || 'Non-EKS'

    if (stgFollowUpReadOnly) {
        const fmtSso = (raw: unknown) =>
            normalizeSSOUserNamesList(raw)
                .filter((e) => (e.userName ?? '').trim())
                .map((e) =>
                    (e.displayName ?? '').trim()
                        ? `${e.userName} (${e.displayName})`
                        : e.userName
                )
                .join(', ') || '—'

        return (
            <SpaceBetween direction="vertical" size="l">
                <Container header={<Header variant="h3">Account workload type</Header>}>
                    <Box>{formValues.accountWorkloadType || 'Non-EKS'}</Box>
                </Container>
                {formValues.accountTypes?.map(
                    (accountType: { label: string; value: string }, i: number) => (
                        <Container header={<Header variant="h3">{accountType.label}</Header>} key={i}>
                            <ColumnLayout columns={2} variant="text-grid">
                                <div>
                                    <Box variant="awsui-key-label">Distribution List</Box>
                                    {formValues[`${accountType.value}DistributionList`] || '—'}
                                </div>
                                <div>
                                    <Box variant="awsui-key-label">Budget Threshold Alert</Box>
                                    {String(formValues[`${accountType.value}MonthlyBudgetCap`] ?? '—')}
                                </div>
								<div>
									<Box variant="awsui-key-label">Platform Owner</Box>
									{formValues[`${accountType.value}platformOwner`] || '—'}
								</div>
								<div>
									<Box variant="awsui-key-label">SSO user-names</Box>
                                    {fmtSso(formValues[`${accountType.value}SSOUserNames`])}
                                </div>
                            </ColumnLayout>
                        </Container>
                    )
                )}
				<Box color="text-body-secondary" fontSize="body-s">
					These fields are inherited from your approved Non-Production request (read-only).
				</Box>
            </SpaceBetween>
        )
    }

    return (
        <SpaceBetween direction="vertical" size="l">
            <Container
                header={
                    <Header variant="h3">Account Workload Type</Header>
                }
            >
                <FormField
                    description="Select whether this account is for EKS or Non-EKS workloads."
                >
                    <RadioGroup
                        value={accountWorkloadType}
                        items={[
                            { value: 'Non-EKS', label: 'Non-EKS' },
                            { value: 'EKS', label: 'EKS' }
                        ]}
                        onChange={({ detail }) => {
                            setFormValues('accountWorkloadType', detail.value)
                        }}
                    />
                </FormField>
            </Container>
            {formValues.accountTypes?.map(
                (accountType: { label: string; value: string }, i: number) => (
                    <Container
                        header={<Header variant="h3">{accountType.label}</Header>}
                        key={i}
                    >
                        <SpaceBetween direction="vertical" size="l">
                            {step2Fields.map((field: StepConfig, i) => {
                                const accountTypeFieldName = `${accountType.value}${field.fieldName}`

                                const rejectedWarning =
                                    field.fieldName === 'DistributionList'
                                        ? rejectedDistributionLists[accountTypeFieldName]
                                        : null

                                return (
                                    <div key={i}>
                                        <Controller
                                            name={accountTypeFieldName}
                                            control={control}
                                            rules={getFieldTypeRules(field.type, field.fieldName)}
                                            render={(
                                                {
                                                    field: { name, onChange, value },
                                                    fieldState: { error }
                                                }
                                            ) => {
                                                return (
                                                    <FormField
                                                        label={field.label}
                                                        description={field.description}
                                                        info={field.info}
                                                        constraintText={field.constraintText}
                                                        errorText={error?.message}
                                                    >
                                                        {field.type === 'text' && (
                                                            <Input
                                                                placeholder={
                                                                    field.fieldName === 'MonthlyBudgetCap'
                                                                        ? 'Enter Monthly Budget'
                                                                        : 'aws-glo02-<group_name>-<platform_name>-<environment>@globe.com.ph'
                                                                }
                                                                name={name}
                                                                value={value}
                                                                onChange={({ detail }) => {
                                                                    onChange(detail.value)
                                                                    setFormValues(
                                                                        accountTypeFieldName,
                                                                        detail.value
                                                                    )
                                                                    // Trigger validation for distribution list in real-time
                                                                    if (field.fieldName === 'DistributionList') {
                                                                        trigger(accountTypeFieldName)
                                                                    }
                                                                }}
                                                                onBlur={() => {
                                                                    // Trigger validation when user leaves the field
                                                                    if (field.fieldName === 'DistributionList') {
                                                                        trigger(accountTypeFieldName)
                                                                    }
                                                                }}
                                                            />
                                                        )}

                                                        {field.type === 'number' && (
                                                            <Input
                                                                name={name}
                                                                value={value}
                                                                type="number"
                                                                onChange={({ detail }) => {
                                                                    onChange(detail.value)
                                                                    setFormValues(
                                                                        accountTypeFieldName,
                                                                        +detail.value
                                                                    )
                                                                }}
                                                            />
                                                        )}

                                                        {field.type === 'radio' && (
                                                            <RadioGroup
                                                                name={name}
                                                                value={value}
                                                                items={field.items}
                                                                onChange={({ detail }) => {
                                                                    onChange(detail.value)
                                                                    setFormValues(
                                                                        accountTypeFieldName,
                                                                        detail.value
                                                                    )
                                                                }}
                                                            />
                                                        )}
                                                    </FormField>
                                                )
                                            }}
                                        />

                                        {rejectedWarning && (
                                            <div style={{ marginTop: '12px' }}>
                                                <Alert
                                                    type="warning"
                                                    header="Previous Request Was Rejected"
                                                >
                                                    {rejectedWarning.warningMessage}
                                                </Alert>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}

                            <FormInput
                                name={`${accountType.value}platformOwner`}
                                label="Platform Owner"
                                control={control}
                                setFormValues={setFormValues}
                                placeholder="Enter Platform Owner Email"
                                constraintText="Example: <platformOwner>@globe.com.ph"
                                description="Platform Owner responsible for managing and maintaining the AWS resources for the project."
                                rules={{
                                    required: 'Required',
                                    pattern: {
                                        value: /^[a-z.]+@globe\.com\.ph$/,
                                        message:
                                            'Only lowercase letters and periods are allowed before @globe.com.ph'
                                    }
                                }}
                            />

                            <FormField
                                label="SSO user-names"
                                description="Please provide the SSO user-names that will be added to the AWS account. Add each user with Network ID and Display Name. Both are required for every entry."
                                errorText={ssoValidation.errors[accountType.value]}
                                constraintText="Network ID format: &lt;network-id&gt;@globetel.com. Display Name is required."
                            >
                                <SpaceBetween direction="vertical" size="s">
                                    {(normalizeSSOUserNamesList(formValues[`${accountType.value}SSOUserNames`])).map((entry, index) => (
                                        <Grid key={index} gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 2 }]}>
                                            <FormField label="Network_ID">
                                                <Input
                                                    type="text"
                                                    placeholder="<network-id>@globetel.com"
                                                    value={entry.userName}
                                                    onChange={({ detail }) => {
                                                        const list = normalizeSSOUserNamesList(formValues[`${accountType.value}SSOUserNames`])
                                                        const next = [...list]
                                                        next[index] = { ...next[index], userName: detail.value }
                                                        setFormValues(`${accountType.value}SSOUserNames`, next)
                                                    }}
                                                />
                                            </FormField>
                                            <FormField label="Display_Name">
                                                <Input
                                                    type="text"
                                                    placeholder="Display name"
                                                    value={entry.displayName}
                                                    onChange={({ detail }) => {
                                                        const list = normalizeSSOUserNamesList(formValues[`${accountType.value}SSOUserNames`])
                                                        const next = [...list]
                                                        next[index] = { ...next[index], displayName: detail.value }
                                                        setFormValues(`${accountType.value}SSOUserNames`, next)
                                                    }}
                                                />
                                            </FormField>
                                            <Box padding={{ top: 'l' }}>
                                                <Button
                                                    variant="link"
                                                    iconName="remove"
                                                    ariaLabel="Remove"
                                                    onClick={() => {
                                                        const list = normalizeSSOUserNamesList(formValues[`${accountType.value}SSOUserNames`])
                                                        const next = list.filter((_, i) => i !== index)
                                                        setFormValues(`${accountType.value}SSOUserNames`, next)
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    ))}
                                    <Button iconName="add-plus" onClick={() => {
                                        const list = normalizeSSOUserNamesList(formValues[`${accountType.value}SSOUserNames`])
                                        setFormValues(`${accountType.value}SSOUserNames`, [...list, { userName: '', displayName: '' }])
                                    }}>
                                        Add
                                    </Button>
                                </SpaceBetween>
                            </FormField>
                        </SpaceBetween>
                    </Container>
                )
            )}
        </SpaceBetween>
    )
}

export default Step2
